"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AUTOMATION_GOAL_PHASES = exports.AutomationGoalTreeInputSchema = exports.AutomationGoalTreeIssueSchema = exports.AutomationGoalTreeToolResultSchema = exports.AutomationGoalTreeRunbookStepSchema = void 0;
exports.buildAutomationGoalTreeEnvelope = buildAutomationGoalTreeEnvelope;
exports.inferAutomationGoalPhase = inferAutomationGoalPhase;
const zod_1 = require("zod");
const goal_tree_1 = require("./goal-tree");
exports.AutomationGoalTreeRunbookStepSchema = zod_1.z
    .object({
    id: zod_1.z.string().min(1).optional(),
    phase: zod_1.z.string().min(1).optional(),
    toolName: zod_1.z.string().min(1),
    reason: zod_1.z.string().min(1).optional(),
    confirmationGate: zod_1.z.string().min(1).optional(),
    expectedOutputs: zod_1.z
        .array(zod_1.z
        .object({
        path: zod_1.z.string().min(1),
        required: zod_1.z.boolean().optional(),
    })
        .strict())
        .optional(),
})
    .strict();
exports.AutomationGoalTreeToolResultSchema = zod_1.z
    .object({
    stepIndex: zod_1.z.number().int().nonnegative(),
    stepId: zod_1.z.string().min(1).nullable().optional(),
    toolName: zod_1.z.string().min(1),
    success: zod_1.z.boolean(),
    affectedRefCount: zod_1.z.number().int().nonnegative().optional(),
})
    .strict();
exports.AutomationGoalTreeIssueSchema = zod_1.z
    .object({
    code: zod_1.z.string().min(1),
    message: zod_1.z.string().min(1),
    phase: zod_1.z.string().min(1).optional(),
    stepIndex: zod_1.z.number().int().nonnegative().optional(),
    stepId: zod_1.z.string().min(1).optional(),
    toolName: zod_1.z.string().min(1).optional(),
    severity: zod_1.z.enum(['blocking', 'warning']).default('blocking'),
    details: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).optional(),
})
    .strict();
exports.AutomationGoalTreeInputSchema = zod_1.z
    .object({
    objective: zod_1.z.string().min(1),
    docId: zod_1.z.string().min(1).optional(),
    blueprintId: zod_1.z.string().min(1).optional(),
    blueprint: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).optional(),
    runbook: zod_1.z.array(exports.AutomationGoalTreeRunbookStepSchema),
    executedStepIndexes: zod_1.z.array(zod_1.z.number().int().nonnegative()).default([]),
    toolResults: zod_1.z.array(exports.AutomationGoalTreeToolResultSchema).default([]),
    validation: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).nullable().optional(),
    issues: zod_1.z.array(exports.AutomationGoalTreeIssueSchema).default([]),
    status: zod_1.z
        .enum([
        'queued',
        'running',
        'working',
        'paused',
        'waiting_for_confirmation',
        'waiting_for_user',
        'completed',
        'failed',
        'cancelled',
    ])
        .optional(),
})
    .strict();
/**
 * The phases of an automation goal. automation_plan_blueprint writes its
 * runbook steps in these phases; the tree shows one node per phase.
 */
exports.AUTOMATION_GOAL_PHASES = [
    'source',
    'trigger',
    'credentials',
    'database',
    'process',
    'output',
    'app_base',
    'validation',
    'publish',
    'marketplace',
    'test',
];
/** Every automation binds a workflow, decides its trigger and is validated. */
const ALWAYS_REQUIRED_PHASES = new Set([
    'process',
    'trigger',
    'validation',
]);
function buildAutomationGoalTreeEnvelope(input) {
    const parsed = exports.AutomationGoalTreeInputSchema.parse(input);
    const executed = new Set(parsed.executedStepIndexes);
    const children = exports.AUTOMATION_GOAL_PHASES.map(phase => buildPhaseNode(parsed, phase, executed)).filter(node => node.required || node.children.length > 0);
    const syntheticChildren = children.length > 0
        ? children
        : [
            {
                nodeId: 'automation.runbook.missing',
                kind: 'missing_runbook',
                label: 'Runbook',
                status: 'missing',
                required: true,
                children: [],
            },
        ];
    const root = {
        nodeId: 'automation.goal',
        kind: 'automation_goal',
        label: parsed.objective,
        status: deriveRootStatus(parsed, syntheticChildren),
        required: true,
        detail: {
            blueprintId: parsed.blueprintId ?? null,
            status: parsed.status ?? null,
        },
        children: syntheticChildren,
    };
    const gaps = collectGaps(root);
    const nextSteps = collectNextSteps(parsed, root);
    return goal_tree_1.GoalTreeEnvelopeSchema.parse({
        schemaVersion: 1,
        domain: 'automation',
        interactionMode: 'agent',
        mode: 'automation_flow',
        target: {
            docId: parsed.docId ?? null,
            blueprintId: parsed.blueprintId ?? null,
        },
        root,
        progress: (0, goal_tree_1.countGoalTreeProgress)(root),
        gaps,
        nextSteps,
        verification: {
            status: gaps.some(gap => gap.blocking) ? 'failed' : 'passed',
            checks: [
                {
                    checkId: 'automation.runbook.executable',
                    label: 'Runbook executable',
                    status: gaps.some(gap => gap.blocking) ? 'failed' : 'passed',
                    reason: gaps.find(gap => gap.blocking)?.reason,
                },
            ],
        },
        uiHints: {
            allowUserPatches: true,
            defaultExpandedDepth: 2,
            orientation: 'vertical',
            renderer: 'streamlain-disclosure-tree',
        },
        payload: {
            blueprint: (0, goal_tree_1.toGoalTreeJsonValue)(parsed.blueprint ?? null),
            runbook: (0, goal_tree_1.toGoalTreeJsonValue)(parsed.runbook),
            validation: (0, goal_tree_1.toGoalTreeJsonValue)(parsed.validation ?? null),
            issues: (0, goal_tree_1.toGoalTreeJsonValue)(parsed.issues),
        },
    });
}
function buildPhaseNode(input, phase, executed) {
    const stepNodes = input.runbook
        .map((step, index) => ({ step, index }))
        .filter(({ step }) => stepPhase(step) === phase)
        .map(({ step, index }) => {
        const result = input.toolResults.find(candidate => candidate.stepIndex === index);
        const status = executed.has(index)
            ? result?.success === false
                ? 'invalid'
                : 'completed'
            : step.confirmationGate
                ? 'blocked'
                : 'ready';
        return {
            nodeId: step.id ?? `automation.runbook.${index}`,
            kind: step.toolName,
            label: step.reason ?? step.toolName,
            status,
            required: true,
            valueSummary: step.toolName,
            detail: (0, goal_tree_1.toGoalTreeJsonValue)({
                stepIndex: index,
                phase,
                toolName: step.toolName,
                expectedOutputs: step.expectedOutputs ?? [],
                confirmationGate: step.confirmationGate ?? null,
                result: result ?? null,
            }),
            actions: automationStepActions(step, index, status),
            children: [],
        };
    });
    const issueNodes = input.issues
        .filter(issue => issuePhase(input, issue) === phase)
        .map((issue, issueIndex) => buildIssueNode(issue, issueIndex));
    const required = phaseRequired(input, phase);
    return {
        nodeId: `automation.phase.${phase}`,
        kind: 'automation_phase',
        label: phaseLabel(phase),
        status: derivePhaseStatus([...stepNodes, ...issueNodes]),
        required,
        actions: automationPhaseActions(phase, required, [
            ...stepNodes,
            ...issueNodes,
        ]),
        children: [...stepNodes, ...issueNodes],
    };
}
function automationStepActions(step, index, status) {
    if (status === 'blocked') {
        return [
            {
                actionId: `${step.id ?? index}.approve`,
                kind: 'approveConfirmation',
                label: 'Approve confirmation',
                requiresConfirmation: true,
            },
        ];
    }
    if (status === 'completed')
        return [];
    return [
        {
            actionId: `${step.id ?? index}.provide`,
            kind: status === 'invalid' ? 'fix_step' : 'provide_input',
            label: status === 'invalid' ? 'Fix step' : 'Provide input',
        },
    ];
}
function automationPhaseActions(phase, required, children) {
    if (!required || children.every(child => child.status === 'completed')) {
        return [];
    }
    return [
        {
            actionId: `automation.phase.${phase}.provide`,
            kind: children.some(child => child.status === 'invalid')
                ? 'fix_step'
                : 'provide_input',
            label: `Provide ${phaseLabel(phase)}`,
        },
    ];
}
function buildIssueNode(issue, issueIndex) {
    const status = issue.severity === 'warning' ? 'invalid' : 'blocked';
    return {
        nodeId: issue.stepId
            ? `automation.issue.${issue.stepId}.${issue.code}`
            : `automation.issue.${issue.stepIndex ?? issueIndex}.${issue.code}`,
        kind: 'automation_issue',
        label: issue.message,
        status,
        required: issue.severity !== 'warning',
        valueSummary: issue.code,
        detail: (0, goal_tree_1.toGoalTreeJsonValue)(issue),
        actions: [
            {
                actionId: `automation.issue.${issue.code}.resolve`,
                kind: 'provide_input',
                label: 'Resolve issue',
            },
        ],
        children: [],
    };
}
function issuePhase(input, issue) {
    if (issue.phase && isAutomationGoalPhase(issue.phase))
        return issue.phase;
    if (typeof issue.stepIndex === 'number') {
        const step = input.runbook[issue.stepIndex];
        if (step) {
            return stepPhase(step);
        }
    }
    if (issue.toolName)
        return inferAutomationGoalPhase(issue.toolName);
    if (issue.code.includes('validation'))
        return 'validation';
    if (issue.code.includes('trigger'))
        return 'trigger';
    if (issue.code.includes('source'))
        return 'source';
    if (issue.code.includes('output'))
        return 'output';
    return 'process';
}
function stepPhase(step) {
    return step.phase && isAutomationGoalPhase(step.phase)
        ? step.phase
        : inferAutomationGoalPhase(step.toolName);
}
function isAutomationGoalPhase(value) {
    return exports.AUTOMATION_GOAL_PHASES.includes(value);
}
/** The phase a runbook step without its own phase belongs to. */
function inferAutomationGoalPhase(toolName) {
    if (toolName.includes('input'))
        return 'source';
    if (toolName.includes('credential') ||
        toolName.includes('oauth') ||
        toolName.includes('secret')) {
        return 'credentials';
    }
    if (toolName.includes('webhook') || toolName.includes('schedule')) {
        return 'trigger';
    }
    if (toolName.includes('output') || toolName.includes('database_create')) {
        return 'output';
    }
    if (toolName.includes('validate'))
        return 'validation';
    if (toolName.includes('publish'))
        return 'publish';
    if (toolName.includes('run'))
        return 'test';
    return 'process';
}
function phaseLabel(phase) {
    return {
        source: 'Source',
        trigger: 'Trigger',
        credentials: 'Credentials',
        process: 'Process',
        output: 'Output',
        database: 'Database',
        app_base: 'App',
        validation: 'Validation',
        publish: 'Publish',
        marketplace: 'Marketplace',
        test: 'Test',
    }[phase];
}
function phaseRequired(input, phase) {
    // The other phases are needed only when the runbook plans a step there
    // (a manual automation has no source, few have a test run).
    return (ALWAYS_REQUIRED_PHASES.has(phase) ||
        input.runbook.some(step => stepPhase(step) === phase));
}
function derivePhaseStatus(children) {
    if (children.length === 0)
        return 'missing';
    if (children.some(node => node.status === 'invalid'))
        return 'invalid';
    if (children.some(node => node.status === 'blocked'))
        return 'blocked';
    if (children.every(node => node.status === 'completed'))
        return 'completed';
    return 'ready';
}
function deriveRootStatus(input, children) {
    if (input.status === 'completed')
        return 'completed';
    if (children.some(node => node.status === 'invalid'))
        return 'invalid';
    if (children.some(node => node.status === 'blocked'))
        return 'blocked';
    if (children.every(node => node.status === 'completed'))
        return 'completed';
    if (children.some(node => node.status === 'missing'))
        return 'missing';
    return input.status === 'running' ? 'running' : 'ready';
}
function collectGaps(root) {
    const gaps = [];
    visit(root, node => {
        const issueSeverity = node.kind === 'automation_issue' ? readIssueSeverity(node) : null;
        const hasGap = node.status === 'missing' ||
            node.status === 'invalid' ||
            (node.kind === 'automation_issue' && node.status === 'blocked');
        if (!hasGap)
            return;
        const blocking = issueSeverity !== null
            ? issueSeverity !== 'warning'
            : node.status === 'invalid' || node.required;
        gaps.push({
            gapId: `${node.nodeId}.gap`,
            nodeId: node.nodeId,
            severity: blocking ? 'blocking' : 'warning',
            blocking,
            reason: node.kind === 'automation_issue'
                ? readIssueReason(node)
                : node.status === 'invalid'
                    ? `${node.label} failed validation`
                    : `${node.label} is missing`,
            allowedActions: node.kind === 'automation_issue'
                ? ['provide_input', 'fix_step']
                : node.status === 'invalid'
                    ? ['fix_step']
                    : ['provide_input'],
        });
    });
    return gaps;
}
function readIssueSeverity(node) {
    const detail = readGoalTreeRecord(node.detail);
    if (detail &&
        (detail.severity === 'blocking' || detail.severity === 'warning')) {
        return detail.severity;
    }
    return null;
}
function readIssueReason(node) {
    const detail = readGoalTreeRecord(node.detail);
    if (detail && typeof detail.message === 'string') {
        return detail.message;
    }
    return `${node.label} must be resolved`;
}
function readGoalTreeRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
        ? value
        : null;
}
function collectNextSteps(input, root) {
    const steps = [];
    visit(root, node => {
        if (node.children.length > 0)
            return;
        if (node.status === 'ready' || node.status === 'blocked') {
            steps.push({
                nextStepId: `${node.nodeId}.next`,
                nodeId: node.nodeId,
                actionKind: node.kind === 'automation_issue'
                    ? 'resolve_issue'
                    : node.status === 'blocked'
                        ? 'approveConfirmation'
                        : 'run',
                label: node.kind === 'automation_issue'
                    ? `Resolve ${node.label}`
                    : node.status === 'blocked'
                        ? 'Approve step'
                        : `Run ${node.label}`,
                requiredConfirmation: node.status === 'blocked' && node.kind !== 'automation_issue'
                    ? `${node.nodeId}.confirmation`
                    : undefined,
            });
        }
    });
    if (steps.length === 0 && input.status !== 'completed') {
        steps.push({
            nextStepId: 'automation.goal.review',
            nodeId: root.nodeId,
            actionKind: 'review',
            label: 'Review automation goal',
        });
    }
    return steps;
}
function visit(node, callback) {
    callback(node);
    for (const child of node.children)
        visit(child, callback);
}
